import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { omitUndefined } from "../lib/optional";

export type typeRadioItem = {
  value: string;
  label: string;
  /** Supporting sentence — what picking this option actually means. */
  description?: string | undefined;
  disabled?: boolean | undefined;
};

export type typeRadioProps = {
  /** The question. Rendered as the group's legend and read before every option. */
  label: string;
  items: readonly typeRadioItem[];
  /** Controlled value; on a route screen this belongs in a query param. */
  value?: string | undefined;
  defaultValue?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  name?: string | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  error?: string | undefined;
  labelHidden?: boolean | undefined;
  /** Horizontal only fits two or three short options; default stacks. */
  orientation?: "vertical" | "horizontal" | undefined;
  className?: string | undefined;
};

/**
 * A single-choice group.
 *
 * Radix owns the roving-focus contract: one tab stop for the whole group,
 * arrow keys move between options and select as they go. Hand-rolling that is
 * where keyboard access usually breaks.
 */
export const Radio = ({
  label,
  items,
  value,
  defaultValue,
  onValueChange,
  name,
  disabled = false,
  required = false,
  error,
  labelHidden = false,
  orientation = "vertical",
  className,
}: typeRadioProps): ReactNode => {
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const errorId = `${generatedId}-error`;
  const invalid = typeof error === "string" && error.length > 0;

  return (
    <div className="flex w-full flex-col gap-2">
      <span
        id={labelId}
        className={cn("text-caption text-ink", labelHidden && "sr-only")}
      >
        {label}
        {required ? (
          <span className="text-error" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </span>

      <RadioGroupPrimitive.Root
        aria-labelledby={labelId}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        disabled={disabled}
        required={required}
        orientation={orientation}
        {...omitUndefined({ value, defaultValue, onValueChange, name })}
        className={cn(
          "flex gap-2",
          orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
          className,
        )}
      >
        {items.map((item) => {
          const itemId = `${generatedId}-${item.value}`;
          const descriptionId = `${itemId}-description`;
          return (
            <label
              key={item.value}
              htmlFor={itemId}
              className={cn(
                "flex min-h-touch-target cursor-pointer items-center gap-3 py-1",
                (item.disabled ?? false) || disabled
                  ? "cursor-not-allowed"
                  : undefined,
              )}
            >
              <RadioGroupPrimitive.Item
                id={itemId}
                value={item.value}
                disabled={item.disabled ?? false}
                aria-describedby={item.description ? descriptionId : undefined}
                className={cn(
                  "flex h-icon-lg w-icon-lg shrink-0 items-center justify-center rounded-full border-2 bg-surface",
                  "transition-colors duration-100 ease-standard",
                  REDUCED_MOTION_SAFE,
                  FOCUS_RING,
                  "data-[state=checked]:border-primary",
                  "disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-bg",
                  invalid ? "border-error" : "border-line",
                )}
              >
                <RadioGroupPrimitive.Indicator className="h-3 w-3 rounded-full bg-primary" />
              </RadioGroupPrimitive.Item>

              <span className="flex flex-col gap-1">
                <span className="text-body text-ink">{item.label}</span>
                {item.description ? (
                  <span
                    id={descriptionId}
                    className="text-caption text-ink-secondary"
                  >
                    {item.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </RadioGroupPrimitive.Root>

      {invalid ? (
        <p id={errorId} role="alert" className="text-caption text-error">
          {error}
        </p>
      ) : null}
    </div>
  );
};
